import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatCard } from "../../components/dash/ui";
import { Input } from "../../components/ui/input";
import { LandStatusPill, CapacityBar, LAND_CATEGORY_SHORT, n, fileUrl } from "./shared";

const LandCard = ({ l, admin }) => (
  <div className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden flex flex-col" data-testid={`land-card-${l.id}`}>
    <div className="h-36 bg-gradient-to-br from-emerald-900 to-emerald-700 relative">
      {l.cover && <img src={fileUrl(l.cover.id)} alt="" className="h-full w-full object-cover" loading="lazy" />}
      <span className="absolute top-3 left-3 bg-white/95 text-slate-900 font-mono text-xs font-bold px-2 py-1 rounded-md" data-testid={`land-code-${l.id}`}>{l.landCode}</span>
      <span className="absolute top-3 right-3"><LandStatusPill status={l.landStatus} testid={`land-status-${l.id}`} /></span>
    </div>
    <div className="p-4 flex-1 flex flex-col gap-3">
      <div>
        <p className="font-bold text-slate-900 text-lg leading-tight">{l.name}</p>
        <p className="text-sm text-slate-600 mt-0.5">{n(l.areaHectares)} ha · Kapasitas {n(l.capacity)} batang</p>
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" />{[l.village, l.district, l.regency].filter(Boolean).join(", ") || "-"}</p>
        {admin && <p className="text-xs text-slate-500 mt-1">Pengelola: <b>{l.farmerGroupName}</b></p>}
        {l.landCategory && <p className="text-xs text-emerald-800 font-semibold mt-1">{LAND_CATEGORY_SHORT[l.landCategory]}</p>}
      </div>
      <CapacityBar land={l} testid={`land-capacity-${l.id}`} />
      <div className="mt-auto flex flex-wrap gap-2 pt-1">
        <Link to={l.id} className="inline-flex h-9 items-center px-4 rounded-full bg-emerald-800 text-white text-sm font-semibold" data-testid={`land-detail-btn-${l.id}`}>Detail</Link>
        {!admin && l.selfManaged && <Link to={`${l.id}/ubah`} className="inline-flex h-9 items-center px-4 rounded-full border border-slate-200 text-sm font-semibold" data-testid={`land-edit-btn-${l.id}`}>Edit</Link>}
        <span className="ml-auto text-xs text-slate-500 self-center">{l.projectCount} kebun</span>
      </div>
    </div>
  </div>
);

export default function LandList() {
  const { user } = useAuth();
  const admin = user.role === "SUPER_ADMIN";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/lands").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  const shown = useMemo(() => (rows || []).filter((l) => `${l.landCode} ${l.name} ${l.farmerGroupName} ${l.village}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const count = (s) => (rows || []).filter((l) => s.includes(l.landStatus)).length;
  return (
    <div data-testid="land-management-page">
      <PageHeader title={admin ? "Pemantauan Lahan" : "Manajemen Lahan"}
        subtitle={admin ? "Pemantauan administratif lahan yang dikelola Kelompok Tani/Koperasi. PT SJI tidak mengesahkan hak atas tanah." : "Kode lahan menjadi patokan untuk kebun, dokumen, foto, dan laporan."}
        action={!admin && <Link to="baru" className="inline-flex h-10 items-center gap-1.5 px-5 rounded-full bg-emerald-800 text-white font-bold" data-testid="add-land-btn"><Plus className="h-4 w-4" />Tambah Lahan</Link>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Lahan" value={rows ? rows.length : "-"} testid="stat-total-land" />
        <StatCard label="Tersedia" value={rows ? count(["AVAILABLE", "PARTIAL"]) : "-"} hint="Tersedia + sebagian terpakai" testid="stat-available-land" />
        <StatCard label="Draft" value={rows ? count(["DRAFT"]) : "-"} testid="stat-draft-land" />
        <StatCard label="Sisa Kapasitas" value={rows ? `${n(rows.filter((l) => ["AVAILABLE", "PARTIAL"].includes(l.landStatus)).reduce((s, l) => s + (l.remainingTrees || 0), 0))}` : "-"} hint="batang" testid="stat-remaining-trees" />
      </div>
      <div className="relative mb-4 max-w-md"><Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" /><Input className="pl-9" placeholder="Cari kode, nama, desa..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="land-search" /></div>
      {rows && !shown.length && <p className="text-sm text-slate-500 py-10 text-center" data-testid="land-empty">{admin ? "Belum ada lahan." : "Belum ada lahan. Tekan Tambah Lahan untuk mulai."}</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{shown.map((l) => <LandCard key={l.id} l={l} admin={admin} />)}</div>
    </div>
  );
}
